import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc, Edit2, Copy, AlertTriangle } from 'lucide-react';
import EditIntegrationFieldDialog from './EditIntegrationFieldDialog';
import CopyIntegrationFieldDialog from './CopyIntegrationFieldDialog';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
    zIndex: 1000,
    paddingTop: '20px', // Add padding from top
    paddingBottom: '20px', // Add padding from bottom
    padding: '20px 0' // Override the existing padding: '0'
  },
  dialog: {
    backgroundColor: 'white',
    padding: '0px 20px 10px 20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    maxHeight: 'calc(100vh - 40px)', // Add maxHeight to account for padding
    overflowY: 'auto', // Ensure scrolling is enabled
    position: 'relative' // Add position relative
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    padding: '10px 0',
    color: '#000'
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const ManageIntegrationFieldsDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  corporateAccount,
  currentProject,
  login_token,
  canUpdateIntegrationFields,
  canDeleteIntegrationFields,
  requirementIdWithPrefix,
  systemId,
  systemType,
  systemName,
  sourceOrProviderSystemName,
  sourceOrProviderSystemId

}) => {
  // Helper function to determine if message is a success message
  const isSuccessMessage = (message) => {
    const lowerMessage = message.toLowerCase();
    return lowerMessage.includes('successfully') ||
           lowerMessage.includes('created') ||
           lowerMessage.includes('updated') ||
           lowerMessage.includes('deleted') ||
           lowerMessage.includes('copied');
  };

  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedFieldForEdit, setSelectedFieldForEdit] = useState(null);
  const [selectedFieldForCopy, setSelectedFieldForCopy] = useState(null);
  const [fieldLevel, setFieldLevel] = useState(() => {
    return selectedRequirements && selectedRequirements.size === 1 ? 'IntegrationLevel' : 'ProjectLevel';
  });
  const [fieldStatuses, setFieldStatuses] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFields, setFilteredFields] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete confirmation dialog state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Create field form state
  const [fieldName, setFieldName] = useState('');
  const [fieldDescription, setFieldDescription] = useState('');
  const [fieldDataType, setFieldDataType] = useState('');
  const [fieldSize, setFieldSize] = useState('');
  const [fieldOptionalOrMandatory, setFieldOptionalOrMandatory] = useState('Optional');
  const [fieldTransformation, setFieldTransformation] = useState('');
  const [fieldDataValidation, setFieldDataValidation] = useState('');
  const [fieldDataSecurity, setFieldDataSecurity] = useState('');
  const [status, setStatus] = useState('Created');
  const [mapsToProviderField, setMapsToProviderField] = useState('');
  const [providerFields, setProviderFields] = useState([]);
  const [isLoadingProviderFields, setIsLoadingProviderFields] = useState(false);

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
      margin: '0',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    thNonSortable: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
  };

  const getIntegrationId = () => {

  // Helper function to determine if message is a success message
  const isSuccessMessage = (message) => {
    return message.toLowerCase().includes('successfully') ||
           message.toLowerCase().includes('created') ||
           message.toLowerCase().includes('updated') ||
           message.toLowerCase().includes('deleted') ||
           message.toLowerCase().includes('copied');
  };
    if (fieldLevel === 'IntegrationLevel' && selectedRequirements.size === 1) {
      return Array.from(selectedRequirements)[0];
    }
    return null;
  };

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setFields([]);
    setSelectedFields(new Set());
    setError('');
    setSuccessMessage('');
    setIsLoading(false);
    setSearchTerm('');
    setFilteredFields([]);
    setCurrentPage(1);
  };

  const resetCreateFormData = () => {
    setFieldName('');
    setFieldDescription('');
    setFieldDataType('');
    setFieldSize('');
    setFieldOptionalOrMandatory('Optional');
    setFieldTransformation('');
    setFieldDataValidation('');
    setFieldDataSecurity('');
    setStatus('Created');
    setMapsToProviderField('');
    setError('');
    setSuccessMessage('');
  };

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to fields
  const sortedFields = React.useMemo(() => {
    let sortableFields = [...filteredFields];
    if (sortConfig.key) {
      sortableFields.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableFields;
  }, [filteredFields, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedFields?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFields = (sortedFields || []).slice(startIndex, startIndex + itemsPerPage);

  const handleEditSuccess = (success) => {
    setIsEditDialogOpen(false);
    setSelectedFieldForEdit(null);

    if (success) {
      setSuccessMessage('Field updated successfully');
      fetchFields();
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
  };

  const handleCopySuccess = (success) => {
    setIsCopyDialogOpen(false);
    setSelectedFieldForCopy(null);

    if (success) {
      setSuccessMessage('Field copied successfully');
      fetchFields();
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
  };

  const handleCreateSuccess = (success) => {
    setIsCreateDialogOpen(false);
    resetCreateFormData();

    if (success) {
      setSuccessMessage('Field created successfully');
      fetchFields();
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
  };

useEffect(() => {
  if (isOpen) {
    resetAllData();

    // Determine the appropriate field level
    const newFieldLevel = selectedRequirements && selectedRequirements.size === 1
      ? 'IntegrationLevel' : 'ProjectLevel';

    // Set the field level without triggering additional effects
    setFieldLevel(newFieldLevel);

    // Make the API calls after a short delay
    setTimeout(() => {
      fetchFields();
      fetchFieldStatuses();
    }, 100);
  }
}, [isOpen, selectedRequirements]);



  // Search and filter effect
  useEffect(() => {
    if (searchTerm) {
      const filtered = fields.filter(field =>
        field.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (field.field_description && field.field_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (field.field_data_type && field.field_data_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (field.field_optional_or_mandatory && field.field_optional_or_mandatory.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (field.status && field.status.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredFields(filtered);
    } else {
      setFilteredFields(fields);
    }
    setCurrentPage(1);
  }, [searchTerm, fields]);

  const fetchFieldStatuses = async () => {
    setIsLoadingStatuses(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statuses = data.entity_statuses.map(s => s.status);
        setFieldStatuses(statuses);

        if (!status && statuses.includes('Created')) {
          setStatus('Created');
        } else if (!status && statuses.length > 0) {
          setStatus(statuses[0]);
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const fetchProviderFields = async () => {
if (!sourceOrProviderSystemId || systemType !== 'CONSUMER') {
    console.log('No sourceOrProviderSystemId provided or systemType is not CONSUMER');
      return;
    }

    setIsLoadingProviderFields(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_integration_requirement_field_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          system_id: sourceOrProviderSystemId,
          system_type: "PROVIDER",
          integration_id: getIntegrationId(),
          filter_by_status: [],
          search_query: "",
          sort_criteria: "field_name"
        })
      });

      const data = await response.json();
      if (data.status === 'Success' && data.integration_fields_list) {
        setProviderFields(data.integration_fields_list || []);
      } else {
        setProviderFields([]);
        console.error('Failed to fetch provider fields:', data.status_description);
      }
    } catch (err) {
      console.error('Error fetching provider fields:', err);
      setProviderFields([]);
    } finally {
      setIsLoadingProviderFields(false);
    }
  };







 // 1. Update the fetchFields function to accept a field level parameter:
const fetchFields = async (levelToUse = null) => {
  resetAllData();
  setIsLoading(true);

  // Use the passed level or fall back to current state
  const currentLevel = levelToUse || fieldLevel;

  if (currentLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) {
    setError('Please select exactly one integration requirement for Integration Level Fields');
    setIsLoading(false);
    return;
  }

  const integrationId = currentLevel === 'IntegrationLevel' && selectedRequirements.size === 1
    ? Array.from(selectedRequirements)[0]
    : null;

  try {
    const response = await fetch(appConfig.getUrl('/get_integration_requirement_field_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        system_id: systemId,
        system_type: systemType,
        integration_id: integrationId,
        filter_by_status: [],
        search_query: "",
        sort_criteria: "updated_date desc"
      })
    });

    const data = await response.json();

    if (data.status === 'Success' && data.integration_fields_list) {
      setFields(data.integration_fields_list || []);
      setFilteredFields(data.integration_fields_list || []);
    } else {
      setFields([]);
      setFilteredFields([]);
      if (data.status !== 'Success') {
        throw new Error(data.status_description || 'Failed to fetch field details');
      }
    }
  } catch (err) {
    setError(err.message);
    console.error('Error fetching fields:', err);
  } finally {
    setIsLoading(false);
  }
};

// 2. Update the main useEffect to pass the determined level directly:
useEffect(() => {
  if (isOpen) {
    resetAllData();

    // Determine the appropriate field level
    const newFieldLevel = selectedRequirements && selectedRequirements.size === 1
      ? 'IntegrationLevel' : 'ProjectLevel';

    // Set the field level
    setFieldLevel(newFieldLevel);

    // Make the API calls with the determined level (don't rely on state)
    setTimeout(() => {
      fetchFields(newFieldLevel); // Pass the level directly
      fetchFieldStatuses();
    }, 100);
  }
}, [isOpen, selectedRequirements]);

// 3. Update the manual field level change effect:
useEffect(() => {
  // Only fetch when field level changes manually (not during initial load)
  if (isOpen && fieldLevel) {
    // Add a small delay to ensure this doesn't conflict with the main useEffect
    const timer = setTimeout(() => {
      fetchFields(fieldLevel);
    }, 150);

    return () => clearTimeout(timer);
  }
}, [fieldLevel]);

// 4. Update the handleFieldLevelChange function:
const handleFieldLevelChange = (newLevel) => {
  if (newLevel !== fieldLevel) {
    resetAllData();
    setFieldLevel(newLevel);
    // The useEffect above will handle the API call
  }
};









const openCreateDialog = () => {
  setIsCreateDialogOpen(true);
  fetchFieldStatuses();
  // Only fetch provider fields if systemType is CONSUMER
  if (systemType === 'CONSUMER') {
    fetchProviderFields();
  }
  resetCreateFormData();
};

  const handleCreateCancel = () => {
    setIsCreateDialogOpen(false);
    resetCreateFormData();
  };

  const handleCreateField = async () => {
  setIsLoading(true);
  setError('');
  setSuccessMessage('');

  if (!fieldName) {
    setError('Please enter a field name');
    setIsLoading(false);
    return;
  }

  if (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) {
    setError('Please select exactly one integration requirement');
    setIsLoading(false);
    return;
  }

  const integrationId = getIntegrationId();

  try {
    const response = await fetch(appConfig.getUrl('/add_integration_requirement_field'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        system_id: systemId,
        system_type: systemType,
        integration_id: integrationId,
        field_name: fieldName,
        field_description: fieldDescription,
        field_data_type: fieldDataType,
        field_size: fieldSize,
        field_optional_or_mandatory: fieldOptionalOrMandatory,
        field_transformation: fieldTransformation,
        field_data_validation: fieldDataValidation,
        field_data_security: fieldDataSecurity,
        maps_to_provider_system_field_name: mapsToProviderField || null,
        status: status
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
      // First show the success message in the error field (same pattern as UpdateStatusDialog)
      setError('Field created successfully');

      // After a delay, close the dialog and refresh data
      setTimeout(async () => {
        setIsCreateDialogOpen(false);
        resetCreateFormData();
        await fetchFields();

        // Set a more permanent success message for the main dialog
        setSuccessMessage('Field created successfully');
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }, 2000);
    } else {
      throw new Error(data.status_description || 'Failed to add integration field');
    }
  } catch (err) {
    setError(err.message);
    console.error('Error adding integration field:', err);
  } finally {
    setIsLoading(false);
  }
};

  // Delete confirmation dialog handler
  const handleDeleteSelectedConfirm = () => {
    if (selectedFields.size === 0) {
      setError('No fields selected for deletion');
      return;
    }
    setIsDeleteConfirmOpen(true);
  };

  // Actual delete function
  const handleDeleteSelected = async () => {
    setIsDeleteConfirmOpen(false);
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    const integrationId = getIntegrationId();
    const promises = [];
    const deletedFieldsCount = selectedFields.size;

    for (const fieldName of selectedFields) {
      promises.push(
        fetch(appConfig.getUrl('/delete_integration_requirement_field'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${login_token}`
          },
          body: JSON.stringify({
            corporate_account: corporateAccount,
            project_id: currentProject,
            system_id: systemId,
            system_type: systemType,
            integration_id: integrationId,
            field_name: fieldName
          })
        })
      );
    }

    try {
      const responses = await Promise.all(promises);
      const results = await Promise.all(responses.map(r => r.json()));

      const failures = results.filter(r => r.status !== 'Success');

      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} fields`);
      }

      // First show the success message in the error field (same pattern as create)
      setError(`Successfully deleted ${deletedFieldsCount} field(s)`);
      setSelectedFields(new Set());

      // After a delay, clear the error message and set the permanent success message
      setTimeout(async () => {
        await fetchFields();

        // Set a more permanent success message for the main dialog
        setSuccessMessage(`Successfully deleted ${deletedFieldsCount} field(s)`);
        setError(''); // Clear the temporary success message from error field

        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }, 2000);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting fields:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedFields.size === paginatedFields.length) {
      setSelectedFields(new Set());
    } else {
      const newSelected = new Set(paginatedFields.map(field => field.field_name));
      setSelectedFields(newSelected);
    }
  };

  const toggleRowSelection = (fieldName) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(fieldName)) {
      newSelected.delete(fieldName);
    } else {
      newSelected.add(fieldName);
    }
    setSelectedFields(newSelected);
  };

  // Delete confirmation dialog component
  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1100}}>
        <div style={{...DialogStyles.dialog, width: '500px'}}>
          <div style={{...DialogStyles.title, backgroundColor: '#FFE6E6', color: '#CC0000', padding: '15px 0'}}>
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle size={20} />
              <span>Confirm Deletion</span>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete {selectedFields.size} selected field{selectedFields.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            {/* Show some details about selected fields */}
            <div className="bg-gray-50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs text-gray-600 mb-2">Selected fields:</p>
              {Array.from(selectedFields).slice(0, 5).map(fieldName => {
                const field = fields.find(f => f.field_name === fieldName);
                return field ? (
                  <div key={fieldName} className="text-xs text-gray-700 mb-1">
                    <strong>{field.field_name}</strong> ({field.field_data_type || 'No type'})
                    {field.field_description && (
                      <div className="text-gray-500 truncate">
                        {field.field_description.length > 50
                          ? `${field.field_description.substring(0, 50)}...`
                          : field.field_description}
                      </div>
                    )}
                  </div>
                ) : null;
              })}
              {selectedFields.size > 5 && (
                <div className="text-xs text-gray-500">
                  ... and {selectedFields.size - 5} more
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-4 p-6 pt-0">
            <button
              onClick={() => setIsDeleteConfirmOpen(false)}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSelected}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Delete {selectedFields.size} Field{selectedFields.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };








 // Updated ManageIntegrationFieldsDialog with fixed header/footer and color-coded sections
const renderCreateFieldDialog = () => {
  if (!isCreateDialogOpen) return null;

  // Updated dialog styles with fixed layout
  const createDialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '700px',
      maxWidth: '90vw',
      height: '90vh',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  return (
    <div style={createDialogStyles.overlay}>
      <div style={createDialogStyles.dialog}>
        {/* Fixed Header */}
        <div style={createDialogStyles.header}>
          Add Integration Field
        </div>

        {/* Scrollable Content */}
        <div style={createDialogStyles.content}>
          {/* ===== FIELD TARGET SECTION ===== */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #e0f2fe',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#0284c7',
              borderBottom: '2px solid #0284c7',
              paddingBottom: '4px'
            }}>
              🎯 Field Target
            </h3>
            <div style={{
              padding: '12px',
              backgroundColor: '#dbeafe',
              borderRadius: '6px',
              border: '1px solid #93c5fd'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#1e40af' }}>
                Adding field to:
              </h4>
              {fieldLevel === 'IntegrationLevel' ? (
                <p style={{ margin: 0, color: '#1e40af' }}>
                  <strong>{requirementIdWithPrefix || 'No requirement selected'}</strong>
                </p>
              ) : (
                <p style={{ margin: 0, color: '#1e40af' }}>
                  Project Level (not tied to any specific integration)
                </p>
              )}
            </div>
          </div>

          {/* ===== BASIC INFORMATION SECTION ===== */}
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #dcfce7',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#059669',
              borderBottom: '2px solid #059669',
              paddingBottom: '4px'
            }}>
              📋 Basic Information
            </h3>

            {/* Field Name */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Field Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
                disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
              />
            </div>

            {/* Field Description */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Field Description:
              </label>
              <textarea
                value={fieldDescription}
                onChange={(e) => setFieldDescription(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                rows={3}
                placeholder="Describe the purpose of this field"
                disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
              />
            </div>
          </div>

          {/* ===== DATA DEFINITION SECTION ===== */}
          <div style={{
            backgroundColor: '#faf5ff',
            border: '1px solid #f3e8ff',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#9333ea',
              borderBottom: '2px solid #9333ea',
              paddingBottom: '4px'
            }}>
              🗃️ Data Definition
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              {/* Data Type */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Data Type:
                </label>
                <select
                  value={fieldDataType}
                  onChange={(e) => setFieldDataType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
                >
                  <option value="">Select data type</option>
                  <option value="String">String</option>
                  <option value="Integer">Integer</option>
                  <option value="Float">Float</option>
                  <option value="Boolean">Boolean</option>
                  <option value="Date">Date</option>
                  <option value="DateTime">DateTime</option>
                  <option value="Binary">Binary</option>
                  <option value="JSON">JSON</option>
                  <option value="XML">XML</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Field Size */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Field Size:
                </label>
                <input
                  type="text"
                  value={fieldSize}
                  onChange={(e) => setFieldSize(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g. 255, 10.2, MAX"
                  disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
                />
              </div>

              {/* Optional or Mandatory */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Optional or Mandatory:
                </label>
                <select
                  value={fieldOptionalOrMandatory}
                  onChange={(e) => setFieldOptionalOrMandatory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
                >
                  <option value="Optional">Optional</option>
                  <option value="Mandatory">Mandatory</option>
                </select>
              </div>
            </div>
          </div>

          {/* ===== PROCESSING RULES SECTION ===== */}
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #fef3c7',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#d97706',
              borderBottom: '2px solid #d97706',
              paddingBottom: '4px'
            }}>
              ⚙️ Processing Rules
            </h3>

            {/* Transformation Rules */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Transformation Rules:
              </label>
              <textarea
                value={fieldTransformation}
                onChange={(e) => setFieldTransformation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                rows={2}
                placeholder="Describe any data transformations"
                disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
              />
            </div>

            {/* Data Validation */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Data Validation:
              </label>
              <textarea
                value={fieldDataValidation}
                onChange={(e) => setFieldDataValidation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                rows={2}
                placeholder="Describe validation rules"
                disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
              />
            </div>
          </div>

          {/* ===== FIELD MAPPING SECTION ===== */}
          {systemType === 'CONSUMER' && (
          <div style={{
            backgroundColor: '#f0f8f0',
            border: '1px solid #d4edda',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#155724',
              borderBottom: '2px solid #155724',
              paddingBottom: '4px'
            }}>
              🔗 Field Mapping
            </h3>

            {/* Maps to Provider Field */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Maps to Provider Field:
              </label>
              {isLoadingProviderFields ? (
                <div style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '4px',
                  color: '#6b7280',
                  fontSize: '14px'
                }}>
                  Loading provider fields...
                </div>
              ) : (
                <select
                  value={mapsToProviderField}
                  onChange={(e) => setMapsToProviderField(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
                >
                  <option value="">Select provider field (optional)</option>
                  {providerFields.map(field => (
                    <option key={field.field_name} value={field.field_name}>
                      {field.field_name} {field.field_data_type ? `(${field.field_data_type})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {!sourceOrProviderSystemId && (
                <div style={{
                  marginTop: '5px',
                  fontSize: '12px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  Provider system not specified - field mapping unavailable
                </div>
              )}
            </div>
          </div>
        )}



          {/* ===== SECURITY & STATUS SECTION ===== */}
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#dc2626',
              borderBottom: '2px solid #dc2626',
              paddingBottom: '4px'
            }}>
              🔒 Security & Status
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              {/* Data Security */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Data Security:
                </label>
                <textarea
                  value={fieldDataSecurity}
                  onChange={(e) => setFieldDataSecurity(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  rows={2}
                  placeholder="Describe security requirements"
                  disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
                />
              </div>

              {/* Status */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Status:
                </label>
                {isLoadingStatuses ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    Loading statuses...
                  </div>
                ) : (
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    disabled={(fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1) || isLoading}
                  >
                    <option value="">Select status</option>
                    {fieldStatuses.map(statusOption => (
                      <option key={statusOption} value={statusOption}>
                        {statusOption}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>


        </div>

        {/* Fixed Footer */}
        <div style={createDialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              onClick={handleCreateCancel}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>

            <div className="relative group">
              <button
                onClick={handleCreateField}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#C8F4DE',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: (isLoading || !canUpdateIntegrationFields || (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1)) ? 0.5 : 1
                }}
                disabled={
                  isLoading ||
                  !canUpdateIntegrationFields ||
                  (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1)
                }
              >
                {isLoading && (
                  <svg style={{
                    animation: 'spin 1s linear infinite',
                    marginLeft: '-4px',
                    marginRight: '8px',
                    width: '16px',
                    height: '16px'
                  }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Creating...' : 'Create Field'}
              </button>

              {/* Hover tooltip for disabled state */}
              {(!canUpdateIntegrationFields || (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1)) && !isLoading && (
                <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  {!canUpdateIntegrationFields
                    ? "Insufficient permission to create fields"
                    : "Select exactly one integration requirement for integration level fields"
                  }
                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '1500px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0',
          margin: '0'
        }}>

          {/* Header */}
          <div className="px-6 pt-0 pb-0">
            <div className="flex items-center mb-4 mt-0 justify-between bg-[#CCCCFF] p-3 rounded">
              <h1 style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'black',
                margin: 0
              }}>
                {fieldLevel === 'IntegrationLevel' && selectedRequirements.size === 1
                  ? `Manage Integration Fields - ${requirementIdWithPrefix || 'No requirement selected'} (${systemType} system ${systemName})`
                  : 'Manage Project Level Integration Fields'}
              </h1>

              <div className="relative group">
                <button
                  className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${(!canUpdateIntegrationFields || (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1)) ? 'opacity-50' : ''}`}
                  onClick={openCreateDialog}
                  disabled={!canUpdateIntegrationFields || (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1)}
                >
                  <Plus size={18} className="mr-2" />
                  Add New Field
                </button>

                {/* Hover tooltip for disabled state */}
                {(!canUpdateIntegrationFields || (fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1)) && (
                  <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    {!canUpdateIntegrationFields
                      ? "Insufficient permission to create fields"
                      : "Select exactly one integration requirement for integration level fields"
                    }
                    <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Field Level Selection */}
          <div className="px-6 mb-6">
            <div className="p-4 border border-gray-300 rounded-md">
              <h3 className="text-base font-semibold mb-3">Please select the level from the below options:</h3>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="fieldLevel"
                    className="w-5 h-5"
                    checked={fieldLevel === 'ProjectLevel'}
                    onChange={() => handleFieldLevelChange('ProjectLevel')}
                  />
                  <span>Project Level Fields (not tied to any specific integration)</span>
                </label>

                <label className={`flex items-center space-x-2 ${selectedRequirements.size !== 1 ? 'opacity-60' : ''}`}>
                  <input
                    type="radio"
                    name="fieldLevel"
                    className="w-5 h-5"
                    checked={fieldLevel === 'IntegrationLevel'}
                    onChange={() => handleFieldLevelChange('IntegrationLevel')}
                    disabled={selectedRequirements.size !== 1}
                  />
                  <span>
                    {selectedRequirements.size !== 1 ? (
                      'Integration Level Fields : No integration requirement selected'
                    ) : (
                      `Integration Level Fields : ${requirementIdWithPrefix}`
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          <div className="px-6">
            {fieldLevel === 'IntegrationLevel' && selectedRequirements.size !== 1 && (
              <div className="mb-4 p-2 rounded bg-yellow-50 text-yellow-700">
                Please select exactly one integration requirement to view its fields
              </div>
            )}

            {error && (
              <div className={`mb-4 p-2 rounded ${
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              }`}>
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
                {successMessage}
              </div>
            )}

            {fields.length === 0 && !isLoading && !error && (
              <div className="mb-4 p-2 rounded bg-yellow-50 text-yellow-700">
                No fields found at this level
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto px-6">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-2 text-left">
                    <input
                      type="checkbox"
                      checked={fields.length > 0 && selectedFields.size === fields.length}
                      onChange={(e) => {
                        const newSelected = new Set();
                        if (e.target.checked) {
                          fields.forEach(field => newSelected.add(field.field_name));
                        }
                        setSelectedFields(newSelected);
                      }}
                      disabled={fields.length === 0}
                    />
                  </th>
                  <th className="p-2 text-left">Field Name</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Data Type</th>
                  <th className="p-2 text-left">Size</th>
                  <th className="p-2 text-left">Optional/Mandatory</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8">Loading...</td>
                  </tr>
                ) : fields.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-500">
                      No fields found.
                    </td>
                  </tr>
                ) : (
                  fields.map(field => (
                    <tr key={field.field_name} className="border-t hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedFields.has(field.field_name)}
                          onChange={() => {
                            const newSelected = new Set(selectedFields);
                            if (newSelected.has(field.field_name)) {
                              newSelected.delete(field.field_name);
                            } else {
                              newSelected.add(field.field_name);
                            }
                            setSelectedFields(newSelected);
                          }}
                        />
                      </td>
                      <td className="p-2">{field.field_name}</td>
                      <td className="p-2">{field.field_description}</td>
                      <td className="p-2">{field.field_data_type}</td>
                      <td className="p-2">{field.field_size}</td>
                      <td className="p-2">{field.field_optional_or_mandatory}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          field.status === 'Active' ? 'bg-green-100 text-green-800' :
                          field.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          field.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {field.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div className="flex gap-1">
                          <div className="relative group">
                            <button
                              className="p-1 rounded hover:bg-[#FF8F89] disabled:opacity-50 flex items-center justify-center"
                              onClick={() => {
                                setSelectedFieldForEdit(field);
                                setIsEditDialogOpen(true);
                              }}
                              disabled={!canUpdateIntegrationFields}
                              title="Edit Field"
                            >
                              <Edit2 size={16} />
                            </button>

                            {/* Hover tooltip for disabled state - positioned on top */}
                            {!canUpdateIntegrationFields && (
                              <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                                Insufficient permission to edit fields
                                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                              </div>
                            )}
                          </div>

                          <div className="relative group">
                            <button
                              className="p-1 rounded hover:bg-[#FF8F89] disabled:opacity-50 flex items-center justify-center"
                              onClick={() => {
                                setSelectedFieldForCopy(field);
                                setIsCopyDialogOpen(true);
                              }}
                              disabled={!canUpdateIntegrationFields}
                              title="Copy Field"
                            >
                              <Copy size={16} />
                            </button>

                            {/* Hover tooltip for disabled state - positioned on top */}
                            {!canUpdateIntegrationFields && (
                              <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                                Insufficient permission to copy fields
                                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {!isLoading && sortedFields.length > 0 && (
            <div className="mx-6 mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Entries: {sortedFields.length}</span>
                <span>| Showing {sortedFields.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedFields.length)} of {sortedFields.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer with action buttons */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <div className="relative group">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      backgroundColor: (isLoading || selectedFields.size === 0 || !canDeleteIntegrationFields) ? '#FF8F89' : '#dc3545',
                      opacity: (isLoading || selectedFields.size === 0 || !canDeleteIntegrationFields) ? 0.6 : 1,
                      cursor: 'pointer'
                    }}
                    onClick={handleDeleteSelectedConfirm}
                    disabled={isLoading || selectedFields.size === 0 || !canDeleteIntegrationFields}
                  >
                    {isLoading ? 'Deleting...' : `Delete Selected (${selectedFields.size})`}
                  </button>

                  {/* Hover tooltip for disabled state */}
                  {(selectedFields.size === 0 || !canDeleteIntegrationFields) && !isLoading && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {selectedFields.size === 0
                        ? "Select fields to delete"
                        : "Insufficient permission to delete fields"
                      }
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedFields.size > 0 ? `${selectedFields.size} selected` : ''}
                </span>
                <button
                  style={{
                    backgroundColor: '#FFFDD0',
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Field Dialog */}
        {selectedFieldForEdit && (
          <EditIntegrationFieldDialog
            isOpen={isEditDialogOpen}
            onClose={handleEditSuccess}
            selectedRequirements={fieldLevel === 'IntegrationLevel' ? selectedRequirements : new Set()}
            field={selectedFieldForEdit}
            corporateAccount={corporateAccount}
            currentProject={currentProject}
            login_token={login_token}
            systemId={systemId}
            systemType= {systemType}
            sourceOrProviderSystemId = {sourceOrProviderSystemId}
            sourceOrProviderSystemName = {sourceOrProviderSystemName}
            isProjectLevel={fieldLevel === 'ProjectLevel'}
          />
        )}

        {/* Copy Field Dialog */}
        {selectedFieldForCopy && (
          <CopyIntegrationFieldDialog
            isOpen={isCopyDialogOpen}
            onClose={handleCopySuccess}
            selectedRequirements={selectedRequirements}
            field={selectedFieldForCopy}
            corporateAccount={corporateAccount}
            currentProject={currentProject}
            login_token={login_token}
            systemId={systemId}
            systemType= {systemType}
            isProjectLevel={fieldLevel === 'ProjectLevel'}
          />
        )}

        {/* Create Field Dialog */}
        {renderCreateFieldDialog()}

        {/* Delete Confirmation Dialog */}
        {renderDeleteConfirmDialog()}
      </div>
    </div>
  );
};

export default ManageIntegrationFieldsDialog;